import { cn } from "@-/com.npmjs.cn";
import type { ComponentType } from "react";
import { APP_STORE_URL, GOOGLE_PLAY_URL, type StorePlatform } from "#website-store-links";
import { useWebsiteStorePlatform } from "#use-website-store-platform";

const BADGE_CLASS_NAME =
  "inline-flex h-12 shrink-0 items-center gap-2 rounded-xs border border-white/25 bg-black pr-3.5 pl-3 font-website text-white no-underline shadow-lg shadow-black/20 transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-website-accent sm:h-14 sm:gap-2.5 sm:pr-4.5 sm:pl-3.5";

function AppleLogo() {
  return (
    <svg
      aria-hidden="true"
      className="h-6 w-auto shrink-0 sm:h-7"
      fill="currentColor"
      viewBox="0 0 24 24"
    >
      <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
    </svg>
  );
}

function GooglePlayLogo() {
  return (
    <svg aria-hidden="true" className="h-5.5 w-auto shrink-0 sm:h-6.5" viewBox="0 0 24 24">
      <path
        d="M1.337.924a1.486 1.486 0 0 0-.112.568v21.017c0 .217.045.419.124.6l11.155-11.087L1.337.924z"
        fill="#4285f4"
      />
      <path
        d="M13.544 10.989l3.258-3.238L3.45.195a1.466 1.466 0 0 0-.946-.179l11.04 10.973z"
        fill="#34a853"
      />
      <path
        d="M13.544 13.056l-11 10.933c.298.036.612-.016.906-.183l13.324-7.54-3.23-3.21z"
        fill="#ea4335"
      />
      <path
        d="M22.018 13.298l-3.919 2.218-3.515-3.493 3.543-3.521 3.891 2.202a1.49 1.49 0 0 1 0 2.594z"
        fill="#fbbc04"
      />
    </svg>
  );
}

export function WebsiteAppStoreBadge() {
  return (
    <a
      aria-label="Download Haniverse on the App Store"
      className={BADGE_CLASS_NAME}
      href={APP_STORE_URL}
    >
      <AppleLogo />
      <span className="flex flex-col items-start">
        <span className="text-[0.6rem] leading-none font-medium tracking-[0.01em] sm:text-[0.66rem]">
          Download on the
        </span>
        <span className="mt-0.5 text-[1.15rem] leading-none font-semibold tracking-[-0.02em] sm:text-[1.32rem]">
          App Store
        </span>
      </span>
    </a>
  );
}

export function WebsiteGooglePlayBadge() {
  return (
    <a
      aria-label="Get Haniverse on Google Play"
      className={BADGE_CLASS_NAME}
      href={GOOGLE_PLAY_URL}
    >
      <GooglePlayLogo />
      <span className="flex flex-col items-start">
        <span className="text-[0.56rem] leading-none font-medium tracking-[0.06em] uppercase sm:text-[0.62rem]">
          Get it on
        </span>
        <span className="mt-0.5 text-[1.15rem] leading-none font-semibold tracking-[-0.02em] sm:text-[1.32rem]">
          Google Play
        </span>
      </span>
    </a>
  );
}

const BADGE_BY_PLATFORM: Record<StorePlatform, ComponentType> = {
  android: WebsiteGooglePlayBadge,
  ios: WebsiteAppStoreBadge,
};

/**
 * Both store badges, the visitor's own store first. Every visitor sees both (an iPhone owner may
 * be sending the link to an Android friend), so reordering never shifts the layout.
 */
export function WebsiteStoreBadges({ className }: { className?: string }) {
  const platform = useWebsiteStorePlatform();
  const order: StorePlatform[] = platform === "android" ? ["android", "ios"] : ["ios", "android"];

  return (
    <div className={cn("flex flex-wrap items-center gap-2.5 sm:gap-3", className)}>
      {order.map((storePlatform) => {
        const Badge = BADGE_BY_PLATFORM[storePlatform];
        return <Badge key={storePlatform} />;
      })}
    </div>
  );
}
